"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { forumService } from "../components/ForumService";
import { useToast } from "@/app/_module/components/ui/use-toast";

interface Votable {
  id: string;
  score: number;
  myVote: 1 | -1 | null;
  upvotes?: number;
  downvotes?: number;
}

// Same click clears the vote; opposite click flips (a flip moves score by ±2).
export function flipVote<T extends Votable>(t: T, dir: 1 | -1): T {
  const prev = t.myVote ?? 0;
  const next = prev === dir ? 0 : dir;
  return { ...t, myVote: (next || null) as 1 | -1 | null, score: t.score - prev + next };
}

// Applies an updater to every forum cache that may hold the target: feed
// infinite caches (posts + pinned), the post-detail cache, and comment lists.
function patchCaches(queryClient: ReturnType<typeof useQueryClient>, targetId: string, patch: (item: any) => any) {
  // Feed caches: ['forum','posts', {sort, category}] — infinite shape
  queryClient.setQueriesData({ queryKey: ["forum", "posts"] }, (data: any) => {
    if (!data?.pages) return data;
    return {
      ...data,
      pages: data.pages.map((page: any) => ({
        ...page,
        posts: (page.posts || []).map((p: any) => (p.id === targetId ? patch(p) : p)),
        pinned: (page.pinned || []).map((p: any) => (p.id === targetId ? patch(p) : p)),
      })),
    };
  });
  // Post detail: ['forum','post', slugOrId]
  queryClient.setQueriesData({ queryKey: ["forum", "post"] }, (data: any) =>
    data && data.id === targetId ? patch(data) : data,
  );
  // Comment lists: ['forum','comments', postId, sort]
  queryClient.setQueriesData({ queryKey: ["forum", "comments"] }, (data: any) => {
    if (!data?.comments) return data;
    return { ...data, comments: data.comments.map((c: any) => (c.id === targetId ? patch(c) : c)) };
  });
}

// Fully-optimistic voting with snapshot rollback. NO invalidation on settle —
// the server response writes the authoritative {score, myVote} instead, so
// rapid vote-clicking stays butter-smooth (no refetch storms).
export function useVoteMutation() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (input: { targetType: "post" | "comment"; targetId: string; value: 1 | -1 | 0 }) =>
      forumService.vote(input),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: ["forum"] });
      const snapshots = [
        ...queryClient.getQueriesData({ queryKey: ["forum", "posts"] }),
        ...queryClient.getQueriesData({ queryKey: ["forum", "post"] }),
        ...queryClient.getQueriesData({ queryKey: ["forum", "comments"] }),
      ];
      patchCaches(queryClient, input.targetId, (item) => {
        const prev = item.myVote ?? 0;
        const next = input.value;
        return { ...item, myVote: (next || null) as any, score: item.score - prev + next };
      });
      return { snapshots };
    },
    onError: (err: any, _input, ctx) => {
      for (const [key, data] of ctx?.snapshots || []) {
        queryClient.setQueryData(key as any, data);
      }
      // Never revert silently — say why ("You cannot vote on your own content", gating, etc.).
      toast({ title: "Vote didn't count", description: err?.message || "Try again.", variant: "destructive", duration: 5000 });
    },
    onSuccess: (fresh) => {
      patchCaches(queryClient, fresh.targetId, (item) => ({
        ...item,
        score: fresh.score,
        upvotes: fresh.upvotes,
        downvotes: fresh.downvotes,
        myVote: fresh.myVote,
      }));
    },
  });
}
